var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import classNames from 'classnames';
import moment from 'moment-timezone';
import Calendar from 'rc-calendar';
// @ts-ignore
import Picker from 'rc-calendar/lib/Picker';
import React from 'react';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { dateInputFilter } from '../Shared/InputFilters';
import { dateFormatter, monthFormatter } from '../Shared/InputFormatters';
import { iconLight } from '../Styles/Colours';
import { Button, ButtonMode, ButtonVariant } from './Button';
import { ArrowDropDownIcon, CalendarIcon } from './Icons';
import { MaskedTextField } from './MaskedTextField';
import styles from './DatePicker.module.css';
export var DateWaterMark;
(function (DateWaterMark) {
    DateWaterMark["DayDisplay"] = "DD/MM/YYYY";
    DateWaterMark["DayEdit"] = "D/M/YYYY";
    DateWaterMark["Month"] = "MM/YYYY";
})(DateWaterMark || (DateWaterMark = {}));
export var FieldIconMode;
(function (FieldIconMode) {
    FieldIconMode["None"] = "none";
    FieldIconMode["Embed"] = "embed";
})(FieldIconMode || (FieldIconMode = {}));
// Styles for this component are loaded globally via boot.tsx > globalStyles.ts > DatePicker.module.css
var formatDateForDisplay = function (newDate, monthMode, weekMode, readOnly) {
    if (monthMode === void 0) { monthMode = false; }
    if (weekMode === void 0) { weekMode = false; }
    if (readOnly === void 0) { readOnly = false; }
    if (!newDate)
        return '';
    if (monthMode)
        return newDate.format(DateWaterMark.Month);
    if (weekMode) {
        var firstDayOfWeek = newDate.clone().weekday(0);
        var lastDayOfWeek = newDate.clone().weekday(6);
        var firstDayOfWeekFormatted = firstDayOfWeek.format('DD');
        if (!firstDayOfWeek.isSame(lastDayOfWeek, 'month'))
            firstDayOfWeekFormatted = firstDayOfWeek.format('DD MMM');
        return firstDayOfWeekFormatted + " - " + lastDayOfWeek.format('DD MMM YYYY');
    }
    if (readOnly)
        return newDate.format('ddd, DD MMM YYYY');
    return newDate.format(DateWaterMark.DayDisplay);
};
var DatePicker = /** @class */ (function (_super) {
    __extends(DatePicker, _super);
    function DatePicker() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            inputValue: _this.props.value
                ? formatDateForDisplay(_this.props.value, _this.props.monthMode, _this.props.isWeekPicker)
                : '',
            mode: _this.props.monthMode ? 'month' : 'date',
            showPicker: false,
            focused: false
        };
        _this.handleCalendarPick = function (date) {
            var inputValue = formatDateForDisplay(date, !!_this.props.monthMode, !!_this.props.isWeekPicker);
            _this.setState({
                inputValue: inputValue,
                showPicker: false
            });
            if (_this.props.onChange) {
                if (date)
                    _this.props.onChange(moment(date.toDate()));
                else
                    _this.props.onChange(null);
            }
        };
        _this.handlePickerOpenChange = function (open) {
            _this.setState({
                showPicker: open
            });
        };
        _this.handleDisplayPicker = function () {
            if (_this.props.readOnly)
                return;
            _this.setState({
                showPicker: true
            });
        };
        _this.handleInputChange = function (value) {
            _this.setState({
                inputValue: value || ''
            });
            if (value) {
                var dateAsMoment = moment(value, _this.props.monthMode ? DateWaterMark.Month : DateWaterMark.DayEdit, true);
                if (_this.isAcceptableDate(dateAsMoment)) {
                    _this.props.onChange(dateAsMoment);
                }
            }
            else if (!value) {
                _this.props.onChange(null);
            }
        };
        _this.handleInputFocus = function () {
            _this.setState({
                focused: true
            });
        };
        _this.handleInputBlur = function (e) {
            var inputValue = _this.props.value
                ? formatDateForDisplay(_this.props.value, !!_this.props.monthMode, !!_this.props.isWeekPicker)
                : '';
            _this.setState({
                focused: false,
                inputValue: inputValue
            });
            if (_this.props.onBlur)
                _this.props.onBlur(e);
        };
        _this.handleClear = function (e) {
            _this.setState({
                focused: true,
                inputValue: ''
            });
            if (_this.props.onClear) {
                _this.props.onClear(e);
            }
        };
        _this.disabledDate = function (current) {
            if (_this.props.disabledDate)
                return _this.props.disabledDate(current);
            return false;
        };
        _this.handlePanelChange = function (date, mode) {
            var newMoment = date ? (_this.props.monthMode ? date.clone().endOf('month') : date.clone()) : null;
            var newMode = _this.props.monthMode && mode === 'date' && _this.state.mode === 'year' ? 'month' : mode;
            var showPicker = !(_this.props.monthMode && newMode === 'date');
            if (_this.props.monthMode && !showPicker)
                newMode = 'month';
            _this.setState({
                mode: newMode,
                inputValue: formatDateForDisplay(newMoment, !!_this.props.monthMode, !!_this.props.isWeekPicker),
                showPicker: showPicker
            });
            if (_this.props.onChange) {
                if (newMoment)
                    _this.props.onChange(moment(newMoment.toDate()));
                else
                    _this.props.onChange(null);
            }
        };
        _this.renderDate = function (current, value) {
            var date = current.date();
            var selectedDate = moment(_this.state.inputValue, ValidDateFormats.Edit).isSame(current, 'day');
            return (React.createElement("div", { className: styles.datepickerDate, "aria-selected": selectedDate, "aria-disabled": 'false' }, date));
        };
        _this.isAcceptableDate = function (date) {
            // In the back end, the Date/DateTime object has a date range of
            // 1st Jan 0001 to 31st Dec 9999.  As such, any dates with the
            // year of 0000 are not valid/acceptable as it causes issues in
            // the back end.
            return !!date && date.isValid() && date.year() !== 0;
        };
        return _this;
    }
    DatePicker.prototype.componentDidUpdate = function (prevProps, prevState, snapshot) {
        if (this.state.focused)
            return;
        var newInputValue = this.props.value
            ? formatDateForDisplay(this.props.value, !!this.props.monthMode, !!this.props.isWeekPicker)
            : '';
        if (this.state.inputValue === newInputValue)
            return;
        this.setState({
            inputValue: newInputValue
        });
    };
    DatePicker.prototype.render = function () {
        var _this = this;
        var clearable = this.props.isClearable === undefined || this.props.isClearable;
        var dropdownClass = classNames({
            'datepicker-dropdown': true,
            'week-picker': this.props.isWeekPicker
        });
        var calendar = (React.createElement(Calendar, { showOk: false, showDateInput: false, dateRender: this.renderDate, defaultValue: moment(), disabledDate: this.disabledDate, onPanelChange: this.handlePanelChange, "data-test-id": "datePicker_calendar_" + this.props.testId, mode: this.state.mode, className: this.props.isWeekPicker ? 'week-picker' : '', showWeekNumber: this.props.showWeek }));
        var buttonClass = classNames('dropdownButton', {
            'no-bg': !!this.props.noBackground
        });
        var inputValue = this.state.inputValue;
        return (React.createElement("div", { className: this.props.useButton ? 'datepicker' : 'datepicker-container' },
            !this.props.useButton && (React.createElement(MaskedTextField, { id: this.props.id, fullWidth: this.props.fullWidth, value: inputValue, placeholder: this.props.placeholder, label: this.props.label, onChange: this.handleInputChange, onBlur: this.handleInputBlur, onFocus: this.handleInputFocus, onClear: clearable ? this.handleClear : undefined, filterFunction: dateInputFilter, readOnly: this.props.readOnly, formatFunction: this.props.monthMode ? monthFormatter : dateFormatter, errorText: this.props.errorText, warningText: this.props.warningText, helperText: this.props.helperText, hideWarningEmptyHelperText: this.props.hideWarningEmptyHelperText, required: this.props.required, disabled: this.props.disabled, align: this.props.align, testId: "datePicker_textfield_" + this.props.testId, clearEmbeddedIcon: this.props.calendarIconMode === FieldIconMode.Embed, fillContainer: !!this.props.fillContainer })),
            !this.props.readOnly && (React.createElement(Picker, { calendar: calendar, className: dropdownClass, value: this.props.value || undefined, onChange: this.handleCalendarPick, onOpenChange: this.handlePickerOpenChange, animation: 'slide-up', open: this.state.showPicker, placement: this.props.useButton ? 'bottomLeft' : 'bottomRight' }, function (_a) {
                var _b;
                var value = _a.value;
                if (_this.props.useButton)
                    return (React.createElement(Button, { startIcon: React.createElement(ArrowDropDownIcon, { size: '24' }), variant: ButtonVariant.primary, mode: ButtonMode.text, className: buttonClass, testId: "datePicker_button_" + _this.props.testId, disabled: _this.props.readOnly, onClick: _this.handleDisplayPicker }, formatDateForDisplay(value, !!_this.props.monthMode, !!_this.props.isWeekPicker, true)));
                if (_this.props.calendarIconMode !== FieldIconMode.None) {
                    var buttonClasses = classNames('buttonCalendar', styles.calendarButton, (_b = {},
                        _b[styles.embedIcon] = _this.props.calendarIconMode === FieldIconMode.Embed,
                        _b[styles.noLabel] = !_this.props.label,
                        _b[styles.noHelperText] = _this.props.hideWarningEmptyHelperText && !_this.props.errorText,
                        _b));
                    return (React.createElement(Button, { className: buttonClasses, onClick: _this.handleDisplayPicker, disabled: _this.props.disabled, mode: ButtonMode.text, iconOnly: true, testId: "datePicker_button_" + _this.props.testId, noShadowOnHover: true, subtleShadeOnHover: true },
                        React.createElement(CalendarIcon, { color: iconLight })));
                }
                return React.createElement(React.Fragment, null);
            }))));
    };
    return DatePicker;
}(React.Component));
export { DatePicker };
var InlineDatePicker = /** @class */ (function (_super) {
    __extends(InlineDatePicker, _super);
    function InlineDatePicker() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            inputValue: _this.props.value
                ? formatDateForDisplay(_this.props.value, _this.props.monthMode, _this.props.isWeekPicker)
                : '',
            mode: _this.props.monthMode ? 'month' : 'date',
            showPicker: false,
            focused: false
        };
        _this.handleCalendarPick = function (date) {
            var inputValue = formatDateForDisplay(date, !!_this.props.monthMode, !!_this.props.isWeekPicker);
            _this.setState({
                inputValue: inputValue,
                showPicker: false
            });
            if (_this.props.onChange) {
                if (date)
                    _this.props.onChange(moment(date.toDate()));
                else
                    _this.props.onChange(null);
            }
        };
        _this.handleDisplayPicker = function () {
            _this.setState({
                showPicker: true
            });
        };
        _this.disabledDate = function (current) {
            if (_this.props.disabledDate)
                return _this.props.disabledDate(current);
            return false;
        };
        return _this;
    }
    InlineDatePicker.prototype.componentDidUpdate = function (prevProps, prevState, snapshot) {
        if (this.state.focused)
            return;
        var newInputValue = this.props.value
            ? formatDateForDisplay(this.props.value, !!this.props.monthMode, !!this.props.isWeekPicker)
            : '';
        if (this.state.inputValue === newInputValue)
            return;
        this.setState({
            inputValue: newInputValue
        });
    };
    InlineDatePicker.prototype.render = function () {
        var _this = this;
        var dropdownClass = classNames({
            'datepicker-dropdown': true,
            'week-picker': this.props.isWeekPicker
        });
        var buttonClass = classNames('dropdownButton', {
            'no-bg': !!this.props.noBackground
        });
        return (React.createElement("div", { className: 'datepicker' },
            React.createElement(Calendar, { showDateInput: false, className: dropdownClass, value: this.props.value ? moment(this.props.value) : undefined, onChange: this.handleCalendarPick }, function (_a) {
                var value = _a.value;
                return (React.createElement(Button, { startIcon: React.createElement(ArrowDropDownIcon, { size: '24' }), mode: ButtonMode.text, className: buttonClass, onClick: _this.handleDisplayPicker, testId: 'pick-date' }, formatDateForDisplay(value, !!_this.props.monthMode, !!_this.props.isWeekPicker, true)));
            })));
    };
    return InlineDatePicker;
}(React.Component));
export { InlineDatePicker };
